import React, { useState, useEffect } from 'react';
import * as S from './AccessBanner.style';
import { useStore } from '../../store/useStore';
import { LoginButton } from '../LoginButton';
import { trackEvent } from '../../services/analytics';

interface Props {
  onUpgradeClick: () => void;
}

export const AccessBanner: React.FC<Props> = ({ onUpgradeClick }) => {
  const { appState, tier, blockedReason, expiresAt, serverNow, previewMode } = useStore();
  const [timeLeft, setTimeLeft] = useState<string>('');

  useEffect(() => {
    if (!expiresAt || !serverNow) return;

    const serverTime = new Date(serverNow).getTime();
    const expiresTime = new Date(expiresAt).getTime();
    const clientNow = Date.now();
    const offset = clientNow - serverTime;

    const updateCountdown = () => {
      const now = Date.now() - offset;
      const remaining = expiresTime - now;

      if (remaining <= 0) {
        setTimeLeft('00:00');
        return;
      }

      const hours = Math.floor(remaining / (1000 * 60 * 60));
      const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((remaining % (1000 * 60)) / 1000);

      if (hours > 0) {
        setTimeLeft(
          `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds
            .toString()
            .padStart(2, '0')}`,
        );
      } else {
        setTimeLeft(
          `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`,
        );
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [expiresAt, serverNow]);

  if (appState === 'loading' || tier === 'active') {
    return null;
  }

  if (tier === 'blocked' && blockedReason === 'new') {
    return null;
  }

  const tierLabel =
    tier === 'preview'
      ? `preview${previewMode ? ` (${previewMode.lag_seconds}s lag)` : ''}`
      : tier === 'trial'
      ? 'trial'
      : tier === 'blocked'
      ? 'preview ended'
      : tier;

  const handleUpgradeClick = () => {
    if (tier === 'preview') {
      trackEvent('access_banner_upgrade_click_preview');
    } else if (tier === 'trial') {
      trackEvent('access_banner_upgrade_click_trial');
    } else if (tier === 'blocked') {
      trackEvent('access_banner_upgrade_click_blocked');
    } else {
      trackEvent('access_banner_upgrade_click_other');
    }
    onUpgradeClick();
  };

  return (
    <S.Banner>
      <S.LeftActions>
        <S.TierBadge>{tierLabel}</S.TierBadge>
        {tier === 'preview' && <S.Countdown>{timeLeft}</S.Countdown>}
        {(tier === 'preview' || tier === 'blocked') && (
          <S.UpgradeLink onClick={handleUpgradeClick}>
            {tier === 'blocked' ? '[ start free trial ]' : '[ unlock 24h trial ]'}
          </S.UpgradeLink>
        )}
        {tier === 'trial' && (
          <S.UpgradeLink onClick={handleUpgradeClick}>[ upgrade to pro ]</S.UpgradeLink>
        )}
      </S.LeftActions>
      <S.RightActions>
        {(tier === 'preview' || tier === 'blocked') && <LoginButton />}
        {tier === 'trial' && <S.Countdown>{timeLeft}</S.Countdown>}
      </S.RightActions>
    </S.Banner>
  );
};
